// src/pages/CommunicationsPage.js
import React, { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import DashboardLayout from "../../components/test/DashboardLayout";
import CommunicationsFilterBar from "../../components/communications/CommunicationsFilterBar";
import CommunicationsTable from "../../components/communications/CommunicationsTable";
import MessageFormModal from "../../components/modals/MessageFormModal";
import {useParams} from "react-router-dom";

const CommunicationsPage = () => {
    const {userId, businessId} = useParams();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null); // Null for 'create', object for 'edit'
    const [filterStatus, setFilterStatus] = useState('All');
    const [filterType, setFilterType] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');

    // Mock Communications Data
    const [messages, setMessages] = useState([
        {
            id: 'msg-001',
            name: 'Welcome Email for New Members',
            type: 'Email',
            subject: 'Welcome to LocalRewards Rewards!',
            audience: 'New Sign-ups',
            status: 'Sent',
            sentDate: '2025-05-28',
            scheduledDate: null,
            opens: 850,
            clicks: 120,
            sentCount: 1000
        },
        {
            id: 'msg-002',
            name: 'Exclusive Gold Tier Offer',
            type: 'Email',
            subject: 'Your Exclusive Gold Tier Discount Inside!',
            audience: 'Gold Tier Members',
            status: 'Scheduled',
            sentDate: null,
            scheduledDate: '2025-06-15',
            opens: 0,
            clicks: 0,
            sentCount: 0
        },
        {
            id: 'msg-003',
            name: 'Points Reminder SMS',
            type: 'SMS',
            subject: null, // SMS usually doesn't have a subject
            audience: 'All Members',
            status: 'Sent',
            sentDate: '2025-06-01',
            scheduledDate: null,
            opens: null, // SMS doesn't track opens
            clicks: 50,
            sentCount: 2000
        },
        {
            id: 'msg-004',
            name: 'Inactive Customer Re-engagement',
            type: 'Email',
            subject: 'We Miss You! Here\'s a Special Offer',
            audience: 'Inactive Customers',
            status: 'Draft',
            sentDate: null,
            scheduledDate: null,
            opens: 0,
            clicks: 0,
            sentCount: 0
        },
        {
            id: 'msg-005',
            name: 'New Product Announcement',
            type: 'Email',
            subject: 'Introducing Our Newest Products!',
            audience: 'All Members',
            status: 'Sent',
            sentDate: '2025-05-20',
            scheduledDate: null,
            opens: 1500,
            clicks: 300,
            sentCount: 2500
        },
    ]);

    const availableStatuses = ['All', 'Sent', 'Scheduled', 'Draft', 'Failed'];
    const availableTypes = ['All', 'Email', 'SMS']; // Could add 'Push Notification'

    // Mock audience options for the form
    const availableAudiences = [
        'All Members',
        'New Sign-ups',
        'Gold Tier Members',
        'Silver Tier Members',
        'Bronze Tier Members',
        'Inactive Customers (last 90 days)',
        'Customers with 0 points'
    ];

    const filteredMessages = messages.filter(msg => {
        const matchesSearch = (msg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            msg.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            msg.audience?.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesStatus = filterStatus === 'All' || msg.status === filterStatus;
        const matchesType = filterType === 'All' || msg.type === filterType;
        return matchesSearch && matchesStatus && matchesType;
    });

    const handleCreateMessage = () => {
        setSelectedMessage(null); // Ensure no message is selected for creation mode
        setIsModalOpen(true);
    };

    const handleEditMessage = (message) => {
        setSelectedMessage(message);
        setIsModalOpen(true);
    };

    const handleSaveMessage = (messageData) => {
        if (messageData.id) {
            // Update existing message
            setMessages(messages.map(msg => (msg.id === messageData.id ? messageData : msg)));
        } else {
            // Add new message
            const newMessage = {
                ...messageData,
                id: `msg-${Date.now()}`, // Simple unique ID
                status: messageData.scheduledDate ? 'Scheduled' : 'Draft', // Set initial status
                sentDate: null,
                opens: 0,
                clicks: 0,
                sentCount: 0,
                // In a real app, 'Sent' status would be set after actual sending process
            };
            setMessages([...messages, newMessage]);
        }
        setIsModalOpen(false);
        setSelectedMessage(null);
    };

    // For demonstration, let's add a function to simulate sending a message
    const handleSendMessage = (id) => {
        setMessages(messages.map(msg =>
            msg.id === id
                ? {
                    ...msg,
                    status: 'Sent',
                    sentDate: new Date().toISOString().slice(0, 10),
                    sentCount: Math.floor(Math.random() * 1000) + 500, // Simulate send count
                    opens: msg.type === 'Email' ? Math.floor(Math.random() * 500) + 100 : null,
                    clicks: Math.floor(Math.random() * 80) + 10,
                }
                : msg
        ));
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedMessage(null);
    };

    return (
        <DashboardLayout userId={userId} businessId={businessId}>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Communications</h1>
                <button
                    onClick={handleCreateMessage}
                    className="bg-indigo-600 text-white px-5 py-2 rounded-lg flex items-center space-x-2 hover:bg-indigo-700 transition duration-200 shadow-md"
                >
                    <PlusIcon className="h-5 w-5" />
                    <span>Create New Message</span>
                </button>
            </div>

            <CommunicationsFilterBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                filterStatus={filterStatus}
                setFilterStatus={setFilterStatus}
                availableStatuses={availableStatuses}
                filterType={filterType}
                setFilterType={setFilterType}
                availableTypes={availableTypes}
            />

            {messages.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md p-8 text-center border-dashed border-2 border-gray-300 mt-6">
                    <p className="text-xl font-semibold text-gray-700 mb-4">No communications sent or drafted yet!</p>
                    <p className="text-gray-500 mb-6">Start engaging your loyalty members by creating your first message.</p>
                    <button
                        onClick={handleCreateMessage}
                        className="bg-indigo-600 text-white px-6 py-3 rounded-lg flex items-center space-x-2 mx-auto hover:bg-indigo-700 transition duration-200 shadow-lg"
                    >
                        <PlusIcon className="h-6 w-6" />
                        <span>Create Your First Message</span>
                    </button>
                </div>
            ) : (
                <CommunicationsTable
                    messages={filteredMessages}
                    onEditMessage={handleEditMessage}
                    onSendMessage={handleSendMessage} // Pass send function
                />
            )}

            <MessageFormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                message={selectedMessage} // Will be null for creation
                onSave={handleSaveMessage}
                availableTypes={availableTypes.filter(type => type !== 'All')}
                availableAudiences={availableAudiences}
            />
        </DashboardLayout>
    );
};

export default CommunicationsPage;